import axios from "axios";
import localforage from "localforage";

import { ActorStore } from "../stores/actorStore";
import { DirectorStore } from "../stores/directorStore";
import { AssetStore } from "../stores/assetStore";
import { CategoriesStore } from "../stores/categoriesStore";
import { GenresStore } from "../stores/genresStore";
import { UserStore } from "../stores/userStore";
import { FavoriteStore } from "../stores/favoriteStore";
import { ProfileStore } from "../stores/profileStore";
import type { LiveChannel } from "../components/app/live2/liveTypes";
import Cookie from "../services/Cookie";

interface ApiResponse {
  data: any;
  error: string | null;
  isCached: boolean;
  server_date?: string | null;
  live?: LiveChannel[] | null;
}

interface AxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: any;
  request?: any;
}

interface CancelToken {
  promise: Promise<Cancel>;
  reason?: Cancel;
  throwIfRequested: () => void;
}

interface Canceler {
  (message?: string): void;
}

interface CancelTokenSource {
  token: CancelToken;
  cancel: Canceler;
}

interface Cancel {
  message?: string;
}

const CACHE_EXPIRATION_TIME = 2 * 60 * 1000; // 2 horas en milisegundos

class TimestampedData {
  data: any;
  timestamp: number;
  language: string;

  constructor(data: any, timestamp: number, language: string) {
    this.data = data;
    this.timestamp = timestamp;
    this.language = language;
  }
}

const do_not_cache = [
  "profiles",
  "v3/profiles",
  "v3/live",
  "live/list",
  "live/home",
  "categories/list/init",
  "v3/favorites",
  "apple/login",
  "v3/asset",
  "login",
  "v3/account",
  "asset/continue",
  "asset/times",
];

class profile {
  profile: string;
  name: string;
  is_kids: boolean;

  constructor(profile: any) {
    this.profile = profile.profile;
    this.name = profile.name;
    this.is_kids = profile.is_kids === "true";
  }
}

export class FlixlatinoApiClient {
  private actorsStore: any;
  private directorStore: any;
  private assetsStore: any;
  private categoriesStore: any;
  private genresStore: any;
  private userStore: any;
  private favoriteStore: any;
  private profileStore: any;
  setup() {
    this.actorsStore = ActorStore();
    this.directorStore = DirectorStore();
    this.assetsStore = AssetStore();
    this.categoriesStore = CategoriesStore();
    this.genresStore = GenresStore();
    this.userStore = UserStore();
    this.favoriteStore = FavoriteStore();
    this.profileStore = ProfileStore();
  }

  private isEmptyParamValue(value: any): boolean {
    return (
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "")
    );
  }

  private setDefaultParam(key: string, value: any) {
    if (!axios.defaults.params) {
      axios.defaults.params = {};
    }

    if (this.isEmptyParamValue(value)) {
      delete axios.defaults.params[key];
      return;
    }

    axios.defaults.params[key] = value;
  }


  private cancelTokenSource: CancelTokenSource | null = null;

  constructor() {
    this.setup();
    if (!axios.defaults.params) {
      axios.defaults.params = {};
    }
    this.setDefaultParam(
      "lang",
      axios.defaults.params.lang ?? this.userStore.selectedLanguage ?? "es"
    );
    this.setDefaultParam("platform", "web");
    this.setDefaultParam("token", this.userStore.getToken);
    this.setDefaultParam("profile", this.profileStore.getCurrentProfile.profile);
  }

  async get(
    endpoint: string,
    lang: string | null = null,
    apiPrefix: string = "api/?l=",
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    console.log("endpoint xx", endpoint, "with lang", lang);
    endpoint = apiPrefix + endpoint;

    this.setDefaultParam("token", this.userStore.getToken);
    this.setDefaultParam("profile", this.profileStore.getCurrentProfile.profile);
    this.setDefaultParam("lang", this.userStore.selectedLanguage);
    // @ts-ignore
    this.setDefaultParam("clarity_user_id", window.clarity_user_id);

    lang = lang ?? axios.defaults.params["lang"];
    const extras =
      "&lang=" + lang + "&profile=" + axios.defaults.params["profile"];

    this.setDefaultParam("lang", lang);
    this.setDefaultParam("platform", "web");

    try {
      if (
        !bypassCache &&
        !do_not_cache.some((prefix) => endpoint.startsWith(apiPrefix + prefix))
      ) {
        const cachedData: TimestampedData = (await localforage.getItem(
          endpoint + extras
        )) as TimestampedData;
        if (
          cachedData !== null &&
          Date.now() - cachedData.timestamp < CACHE_EXPIRATION_TIME &&
          cachedData.language === lang
        ) {
          console.log("cachedData", cachedData);
          return {
            data: cachedData.data,
            error: null,
            isCached: true,
          };
        }
      }

      this.cancelTokenSource = axios.CancelToken.source();
      const response: AxiosResponse = await axios.get(endpoint, {
        // @ts-ignore
        cancelToken: this.cancelTokenSource.token,
      });

      const store = {
        data: response.data,
        timestamp: Date.now(),
        language: axios.defaults.params["lang"],
      };
      if (response.data.status !== "error") {
        await localforage.setItem(endpoint + extras, store, (err) => {
          if (err) {
            console.error(err);
          }
        });
      }

      return {
        data: response.data,
        error: null,
        isCached: false,
      };
    } catch (error) {
      if (axios.isCancel(error)) {
        return {
          data: null,
          error: "Request canceled",
          isCached: false,
        };
      }

      return {
        data: null,
        // @ts-ignore
        error: error.message,
        isCached: false,
      };
    }
  }

  async post(endpoint: string, data: any): Promise<ApiResponse> {
    return this.postRequest(`api/?l=${endpoint}`, data);
  }

  async postV4(endpoint: string, data: any): Promise<ApiResponse> {
    return this.postRequest(`api/v4/${endpoint}`, data);
  }

  private async postRequest(endpoint: string, data: any): Promise<ApiResponse> {
    this.setDefaultParam("token", this.userStore.getToken);

    // @ts-ignore
    if (!this.isEmptyParamValue(window.clarity_user_id)) {
      if (endpoint.includes("?")) {
        // @ts-ignore
        endpoint += "&clarity_user_id=" + window.clarity_user_id;
      } else {
        // @ts-ignore
        endpoint += "?clarity_user_id=" + window.clarity_user_id;
      }
    }

    try {
      const response: AxiosResponse = await axios.post(endpoint, data);
      return {
        data: response.data,
        error: null,
        isCached: false,
      };
    } catch (error) {
      return {
        data: null,
        // @ts-ignore
        error: error.message,
        isCached: false,
      };
    }
  }

  async getProfiles(): Promise<ApiResponse> {
    return await this.get("v3/profiles").then((response) => {
      return response.data;
    });
  }

  async saveProfile(profile: any): Promise<ApiResponse> {
    return await this.post("profiles/handle", profile)
      .then((response) => response.data)
      .then((response) => response.profiles.all);
  }

  cancelRequest() {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("Request canceled by user");
    }
  }
  async getAssetData(
    id: string,
    lang: string | null = null
  ): Promise<ApiResponse> {
    const v4 = await this.getV4(`asset/${id}`, lang);
    const legacy = await this.get("v3/asset" + "&id=" + id, lang);

    const v4Data = this.isAssetPayload(v4?.data) ? v4.data : null;
    const legacyData = this.isAssetPayload(legacy?.data) ? legacy.data : null;

    const selected = this.pickBestAssetPayload(v4Data, legacyData);
    if (selected) {
      this.assetsStore.setAsset(id, selected);
      return selected;
    }

    return v4Data ?? legacyData ?? v4?.data ?? legacy?.data;
  }

  async getInitData(
    profile: object,
    lang: string | null = null
  ): Promise<ApiResponse> {
    this.profileStore.setCurrentProfile(profile);
    const response = await this.getV4("init", lang);

    if (response?.data) {
      this.applyInitData(response.data);
      return response.data;
    }

    const kEndpoint = "kids_v3/init";
    const aEndpoint = "ios_v3/init_split";
    // @ts-ignore
    const endpoint = profile.is_kid === "true" ? kEndpoint : aEndpoint;
    const fallback = await this.get(endpoint, lang);
    if (fallback?.data) {
      this.applyInitData(fallback.data);
      return fallback.data;
    }

    return fallback;
  }

  async linkDevice(code: string): Promise<ApiResponse> {
    return this.post("link/link", {
      code: code,
    }).then((response) => response.data);
  }
  async cancelAccount(reasons: any): Promise<boolean> {
    return this.post("update", {
      cancelsubcription: true,
      reason: reasons,
    }).then((response) => {
      if (response.data.update.cancelsubcription) {
        this.userStore.setAccountStatus("cancelled");
      }

      return response.data.update.cancelsubcription;
    });
  }
  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> {
    console.info("change PAss", oldPassword, newPassword);
    return this.post("update", {
      oldpass: oldPassword,
      newpass: newPassword,
    }).then((response) => {
      if (response.data.status === "failure") {
        return false;
      }
      return true;
    });
  }

  async getFavorites(): Promise<ApiResponse> {
    return this.get("v3/favorites").then((response) => {
      this.favoriteStore.setFavorites(response.data.favorites);
      console.log("favorites", response.data.favorites);
      return response.data;
    });
  }

  async createUnattended(email: string): Promise<ApiResponse> {
    return this.postV4("register/unatended", { email }).then((response) => {
      const payload = response?.data;
      if (payload && typeof payload === "object") {
        return {
          ...payload,
          code: payload?.code ?? 200,
        };
      }

      return {
        data: payload,
        code: 200,
      };
    });
  }

  async magicLink(email: string): Promise<ApiResponse> {
    return this.postV4("magic-link", { email }).then((response) => {
      const payload = response?.data;
      if (payload && typeof payload === "object") {
        return {
          ...payload,
          code: payload?.code ?? 200,
        };
      }

      return {
        data: payload,
        code: 200,
      };
    });
  }

  async getLiveChannels(): Promise<ApiResponse> {
    return this.getV4("live/list?nolimit=1").then((response) => response.data);
  }

  async getHomeLiveChannels(
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.getV4("live/home", lang, bypassCache).then((response) => response.data);
  }

  async getBanners(
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.getV4("banners/", lang, bypassCache).then((response) => response.data);
  }

  async getAllBanners(
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.getV4("banners/all", lang, bypassCache).then((response) => response.data);
  }

  async getAssetBySlug(slug: string): Promise<ApiResponse> {
    return this.get("asset/" + slug).then((r) => {
      const response = r.data;
      this.assetsStore.setAsset(slug, response.movies);

      return response;
    });
  }
  async removeFavorite(id: string, profile: string): Promise<ApiResponse> {
    // @ts-ignore
    const payload = {
      id: id,
      profile: profile,
      remove: true,
    };
    return this.post("favorite", payload).then((response) => response.data);
  }

  async addFavorite(id: string, profile: string): Promise<ApiResponse> {
    // @ts-ignore
    const payload = {
      id: id,
      profile: profile,
    };
    return this.post("favorite", payload).then((response) => response.data);
  }

  async reportTime(profile: any): Promise<ApiResponse> {
    return await this.post("profiles/handle", profile)
      .then((response) => response.data)
      .then((response) => response.profiles.all);
  }

  async getAccount(): Promise<ApiResponse> {
    return this.get("v3/account").then((response) => {
      this.userStore.setAccount(response.data.account);
      return response.data;
    });
  }

  async braintreeUpgrade(action: string): Promise<ApiResponse> {
    const options = {
      action: action,
    };
    return this.post("braintree/upgrade", options).then((response) => {
      return response.data;
    });
  }

  async payWithCard(data: object): Promise<ApiResponse> {
    return this.post("payment", data).then((response) => response.data);
  }

  async getLoggedOutInitData(lang: string | null): Promise<ApiResponse> {
    const endpoint = "init";

    return this.getV4(endpoint, lang).then((r) => {
      const response = r.data;
      const responseLang = response.lang ?? lang ?? this.userStore.getLanguage;
      this.actorsStore.setActors(response.actors);
      this.assetsStore.setAssets(response.movies, responseLang);
      this.categoriesStore.setCategories(response.categories, responseLang);
      this.categoriesStore.setFeatured(response.featured);
      this.genresStore.setGenres(response.genres);
      this.directorStore.setDirectors(response.directors);
      return response;
    });
  }
  async getV4(
    endpoint: string,
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.get(endpoint, lang, "api/v4/", bypassCache);
  }

  async getCountryCategories(country: number) {
    return this.post("categories/list", { country }).then((response) => {
      return response.data;
    });
  }

  async getAsset(id: string, lang: string | null = null): Promise<ApiResponse> {
    return this.getAssetData(id, lang);
  }

  async searchAssets(query: string): Promise<ApiResponse> {
    return this.getV4(`search/${encodeURIComponent(query)}`).then((r) => r.data);
  }

  async getContinueWatching(): Promise<ApiResponse> {
    return this.getV4("asset/continue").then((r) => r.data);
  }

  async syncGuestContinueWatchingToAccount(): Promise<void> {
    if (!this.userStore.isLoggedIn) {
      return;
    }

    const guestEntries = Object.entries(
      Cookie.getGuestContinueWatching() || {}
    ) as Array<[string, Record<string, any>]>;

    if (!guestEntries.length) {
      return;
    }

    const failedEntries: Record<string, Record<string, any>> = {};

    await Promise.allSettled(
      guestEntries.map(async ([key, entry]) => {
        const seconds = Number(entry?.seconds ?? 0);
        const type = String(entry?.type || "movie").toLowerCase();
        const assetId = String(entry?.assetId ?? key ?? "");
        const episodeId = String(entry?.episodeId ?? "");

        if (!Number.isFinite(seconds) || seconds <= 0) {
          return;
        }

        try {
          if (type === "series" && episodeId) {
            await this.post("regpos", {
              episode_id: episodeId,
              type: "series",
              second: seconds,
            });
            return;
          }

          if (assetId) {
            await this.post("regpos", {
              movie: assetId,
              type: "movie",
              second: seconds,
            });
            return;
          }
        } catch (error) {
          failedEntries[key] = entry;
          return;
        }

        failedEntries[key] = entry;
      })
    );

    Cookie.setGuestContinueWatching(failedEntries);
  }

  async getAssetsTimes(): Promise<ApiResponse> {
    return this.getV4("asset/times").then((r) => {
      const serverTimes = Object.assign({}, r.data || {});
      const nextAssetsTimes = Object.assign({}, serverTimes) as Record<string, number>;

      if (this.userStore.isLoggedIn) {
        const guestEntries = Object.entries(
          Cookie.getGuestContinueWatching() || {}
        ) as Array<[string, Record<string, any>]>;

        const syncedGuestCookie = guestEntries.reduce((acc, [key, entry]) => {
          const assetId = String(entry?.assetId ?? "");
          const seconds = Number(entry?.seconds ?? 0);

          if (!assetId || !Number.isFinite(seconds) || seconds <= 0) {
            acc[key] = entry;
            return acc;
          }

          if (Object.prototype.hasOwnProperty.call(serverTimes, assetId)) {
            acc[key] = {
              ...entry,
              seconds: Number(serverTimes[assetId] ?? 0),
            };
            return acc;
          }

          nextAssetsTimes[assetId] = seconds;
          acc[key] = entry;
          return acc;
        }, {} as Record<string, Record<string, any>>);

        Cookie.setGuestContinueWatching(syncedGuestCookie);
      }

      if (typeof this.assetsStore.setAssetsTimes === "function") {
        this.assetsStore.setAssetsTimes(nextAssetsTimes);
      }

      return r.data;
    });
  }

  async getHomeCategoriesInit(
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.getV4("categories/list/init", lang, bypassCache).then((r) => {
      const payload = r.data;
      const responseLang = lang ?? this.userStore.getLanguage;
      const categories = Array.isArray(payload?.categories)
        ? payload.categories
        : Array.isArray(payload)
          ? payload
          : [];
      const largeCategory = categories.find(
        (category: any) => String(category?.layout || "").toLowerCase() === "large"
      );
      const featured = Array.isArray(payload?.featured)
        ? payload.featured
        : Array.isArray(largeCategory?.movies)
          ? largeCategory.movies
          : [];

      this.categoriesStore.setCategories(categories, responseLang);
      this.categoriesStore.setFeatured(featured);
      return payload;
    });
  }

  async getCategories(lang: string | null = null): Promise<ApiResponse> {
    return this.getV4("categories/list", lang).then((r) => {
      this.categoriesStore.setCategories(r.data, lang ?? this.userStore.getLanguage);
      this.categoriesStore.setFeatured(r.data?.[0]?.movies ?? []);
      return r.data;
    });
  }

  async getMetadata(bypassCache: boolean = false): Promise<ApiResponse> {
    return this.getV4("metadata/", null, bypassCache).then((r) => {
      this.genresStore.setGenres(r.data.genres);
      this.actorsStore.setActors(r.data.actors);
      this.directorStore.setDirectors(r.data.directors);
      return r.data;
    });
  }

  async getAssets(
    lang: string | null = null,
    bypassCache: boolean = false
  ): Promise<ApiResponse> {
    return this.getV4("asset/list", lang, bypassCache).then((r) => {
      this.assetsStore.setAssets(r.data, lang ?? this.userStore.getLanguage);
      return r.data;
    });
  }

  async rememberPin(profile: object): Promise<ApiResponse> {
    return this.post("rememberPin", profile).then((r) => r.data);
  }

  private applyInitData(response: any) {
    const responseLang = response.lang ?? this.userStore.getLanguage;
    this.actorsStore.setActors(response.actors);
    this.assetsStore.setAssets(response.movies, responseLang);
    this.categoriesStore.setCategories(response.categories, responseLang);
    this.categoriesStore.setFeatured(response.featured);
    if (response.live) {
      this.categoriesStore.setLive(response.live);
    }
    this.genresStore.setGenres(response.genres);
    this.directorStore.setDirectors(response.directors);
    if (response.favorites?.[0]?.movies) {
      this.favoriteStore.setFavorites(response.favorites[0].movies);
    }
  }

  private isAssetPayload(payload: any): boolean {
    if (!payload || typeof payload !== "object") {
      return false;
    }
    return (
      typeof payload.urlplay === "string" ||
      Array.isArray(payload.seasons) ||
      typeof payload.type === "string"
    );
  }

  private pickBestAssetPayload(v4Data: any, legacyData: any): any {
    if (!v4Data && !legacyData) {
      return null;
    }
    if (v4Data && !legacyData) {
      return v4Data;
    }
    if (!v4Data && legacyData) {
      return legacyData;
    }

    const v4Score = this.assetPlaybackScore(v4Data);
    const legacyScore = this.assetPlaybackScore(legacyData);
    return legacyScore > v4Score ? legacyData : v4Data;
  }

  private assetPlaybackScore(payload: any): number {
    if (!payload || typeof payload !== "object") {
      return 0;
    }

    const movieScore = this.playbackUrlScore(payload.urlplay);
    const episodeScore = Array.isArray(payload.seasons)
      ? payload.seasons.reduce((acc: number, season: any) => {
          const current =
            Array.isArray(season?.episodes) && season.episodes.length
              ? Math.max(
                  ...season.episodes.map((ep: any) =>
                    this.playbackUrlScore(ep?.urlplay)
                  )
                )
              : 0;
          return Math.max(acc, current);
        }, 0)
      : 0;

    return Math.max(movieScore, episodeScore);
  }

  private playbackUrlScore(url: any): number {
    if (typeof url !== "string" || url.length === 0) {
      return 0;
    }
    const normalized = url.split("?")[0].toLowerCase();
    if (normalized.endsWith(".m3u8") || normalized.endsWith(".mp4")) {
      return 3;
    }
    if (normalized.endsWith(".mpd")) {
      return 2;
    }
    if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
      return 1;
    }
    return 0;
  }
}
